import React, { useEffect } from 'react';

const SlideOutPanel = ({ 
  isOpen, 
  onClose, 
  side = 'left', // 'left' or 'right'
  title,
  icon,
  children,
  accentColor = '#00FFFF' // cyan for left, magenta for right
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-out'
        }}
      />

      {/* Slide-out panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          [side]: 0,
          width: '35%',
          height: '100vh',
          background: 'rgba(10, 14, 39, 0.98)',
          border: `2px solid ${accentColor}`,
          borderRadius: side === 'left' ? '0 12px 12px 0' : '12px 0 0 12px',
          boxShadow: `0 0 40px ${accentColor}80`,
          zIndex: 10001,
          display: 'flex',
          flexDirection: 'column',
          animation: `slideIn${side === 'left' ? 'Left' : 'Right'} 0.3s cubic-bezier(0.4, 0, 0.2, 1)`,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 25px',
          borderBottom: `2px solid ${accentColor}40`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${accentColor}10 0%, transparent 100%)`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '18px',
            fontWeight: '700',
            color: accentColor
          }}>
            <span style={{ fontSize: '24px' }}>{icon}</span>
            <span>{title}</span>
          </div>
          
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              border: `1px solid ${accentColor}40`,
              background: 'rgba(0, 0, 0, 0.3)',
              color: accentColor,
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              fontWeight: '700'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `${accentColor}20`;
              e.target.style.boxShadow = `0 0 15px ${accentColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(0, 0, 0, 0.3)';
              e.target.style.boxShadow = 'none';
            }}
          >
            ✕
          </button>
        </div>

        {/* Content area with scroll */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 25px',
          scrollbarWidth: 'thin',
          scrollbarColor: `${accentColor}40 transparent`
        }}>
          {children}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        /* Custom scrollbar for webkit browsers */
        div::-webkit-scrollbar {
          width: 8px;
        }
        div::-webkit-scrollbar-track {
          background: transparent;
        }
        div::-webkit-scrollbar-thumb {
          background: ${accentColor}40;
          border-radius: 4px;
        }
        div::-webkit-scrollbar-thumb:hover {
          background: ${accentColor}60;
        }
      `}</style>
    </>
  );
};

export default SlideOutPanel;
